import DevToolsIcon from '@clearkit/icons/glyphs/Config';
import FitCompany from '@clearkit/icons/glyphs/FitCompany';
import ZapIcon from '@clearkit/icons/glyphs/Zap';
import Clearbit from '@clearkit/icons/identity/Clearbit';
import AudiencesNavIcon from '@clearkit/icons/nav/Audiences';
import CompaniesNavIcon from '@clearkit/icons/nav/Companies';
import DashboardIcon from '@clearkit/icons/nav/Dashboard';
import DataEnrichmentNavIcon from '@clearkit/icons/nav/Demo';
import FormsNavIcon from '@clearkit/icons/nav/Forms';
import HelpIcon from '@clearkit/icons/nav/Help';
import PeopleNavIcon from '@clearkit/icons/nav/People';
import SandboxNavIcon from '@clearkit/icons/nav/Sandbox';
import SetupNavIcon from '@clearkit/icons/nav/Setup';
import SwitcherNavIcon from '@clearkit/icons/nav/Switcher';
import UsageNavIcon from '@clearkit/icons/nav/Usage';
import Popover from '@material-ui/core/Popover';
import RootRef from '@material-ui/core/RootRef';
import classnames from 'classnames';
import { useRef, useState } from 'react';
import { withRouter } from 'react-router';
import { NavLink } from 'react-router-dom';
import { Subscribe } from 'unstated';

import AppSwitch from '~/components/AppSwitch';
import CompanyLogo from '~/components/CompanyLogo';
import UserContainer from '~/containers/User';
import { AccountFlagEnum } from '~/generated/graphql';
import useFeatureFlag from '~/hooks/account/useFeatureFlag';
import { useHasUsageAccess } from '~/hooks/account/usePlatformAccess';
import useVisitorReport from '~/hooks/useVisitorReport';
import paths from '~/lib/paths';

import DeveloperPanel from '../DeveloperPanel';
import styles from './Sidebar.style';
import UserMenu from './sidebar/UserMenu/UserMenu';
import SidebarNavLink from './SidebarNavLink';

function SidebarDivider({ className }) {
  return (
    <div
      className={classnames(className, 'border-gray-100 border-t my-1')}
    ></div>
  );
}

const Sidebar = ({
  classes,
  userContainer,
  history: {
    location: { pathname },
  },
}) => {
  const [configOpen, setConfigOpen] = useState(false);
  const [appSwitchOpen, setAppSwitchOpen] = useState(false);
  const [showDevPanel, setShowDevPanel] = useState(false);
  const { enabled: isVisitorReportEnabled } = useVisitorReport();
  const configMenuButton = useRef();
  const appSwitchButton = useRef();
  const hasUsageFlag = useFeatureFlag(AccountFlagEnum.XShowUsageOnPlatform)
    .enabled;
  const usageEnabled = useHasUsageAccess() && hasUsageFlag;

  const renderMenuContents = () => {
    if (userContainer.hasNoAccess()) {
      return <ul className={classes.list} />;
    }

    if (pathname === '/welcome' || userContainer.needsToOnboard()) {
      return (
        <ul className={classes.list}>
          <SidebarNavLink
            classes={classes}
            className={classes.logoLink}
            icon={SetupNavIcon}
            title="Welcome"
            to="/welcome"
          />
        </ul>
      );
    }

    return (
      <ul className={classnames('flex flex-col flex-none gap-1')}>
        {userContainer.shouldShowDashboard() && (
          <SidebarNavLink
            classes={classes}
            className={classes.logoLink}
            exact
            icon={DashboardIcon}
            title="Home"
            to="/"
          />
        )}

        <SidebarDivider />

        {userContainer.shouldShowPeople() && (
          <SidebarNavLink
            classes={classes}
            icon={PeopleNavIcon}
            title="People"
            to="/people"
          />
        )}

        {userContainer.shouldShowCompanies() && (
          <SidebarNavLink
            classes={classes}
            icon={CompaniesNavIcon}
            title="Companies"
            to="/companies"
          />
        )}

        {userContainer.shouldShowICP() && (
          <SidebarNavLink
            classes={classes}
            icon={FitCompany}
            title="Company Fit Score (Beta)"
            to={paths.idealCustomerProfile()}
          />
        )}

        <SidebarDivider />

        <SidebarNavLink
          classes={classes}
          icon={DataEnrichmentNavIcon}
          title="Enrichment"
          to="/enrichment"
        />

        <SidebarNavLink
          classes={classes}
          icon={FormsNavIcon}
          title="Forms"
          to="/forms"
        />

        <SidebarNavLink
          classes={classes}
          icon={AudiencesNavIcon}
          title="Advertising"
          to={paths.audiences()}
        />

        <SidebarDivider />

        {userContainer.shouldShowSettings() && (
          <SidebarNavLink
            classes={classes}
            icon={ZapIcon}
            iconSize={20}
            title="Connections"
            to="/settings/configuration"
          />
        )}
        {usageEnabled && (
          <SidebarNavLink
            classes={classes}
            icon={UsageNavIcon}
            title="Usage"
            to="/settings/usage"
          />
        )}
        <SidebarDivider />

        <SidebarNavLink
          classes={classes}
          href="https://help.clearbit.com/hc/en-us/"
          icon={HelpIcon}
          rel="noopener noreferrer"
          target="_blank"
          title="Help"
        />

        {isVisitorReportEnabled && (
          <>
            <SidebarDivider />
            <SidebarNavLink
              classes={classes}
              icon={CompaniesNavIcon}
              title="Visitor Report"
              to="/visitor-report"
            />
          </>
        )}
      </ul>
    );
  };

  const domain = userContainer.accountDomain();

  if (
    pathname.startsWith('/onboarding') ||
    (pathname.startsWith('/settings/configuration') &&
      userContainer.hasNoAccess())
  ) {
    return null;
  }

  return (
    <nav className={classnames(classes.root, 'border-r border-gray-100')}>
      <NavLink className={classes.logo} to="/">
        <Clearbit height="36" width="36" />
      </NavLink>

      {renderMenuContents()}

      <div className={classes.spacer} />

      <ul className={classes.lowerList}>
        {process.env.NODE_ENV === 'development' && (
          <>
            <SidebarNavLink
              classes={classes}
              icon={SandboxNavIcon}
              title="Sandbox"
              to="/sandbox"
            />

            <SidebarNavLink
              classes={classes}
              href="#none"
              icon={DevToolsIcon}
              onClick={(e) => {
                e.preventDefault();
                setShowDevPanel(true);
              }}
              title="Dev Tools"
            />

            <DeveloperPanel
              onClose={() => {
                setShowDevPanel(false);
              }}
              open={showDevPanel}
            />
          </>
        )}

        <RootRef rootRef={appSwitchButton}>
          <SidebarNavLink
            classes={classes}
            className={classnames(classes.listItem, classes.lowerListItem)}
            href="#none"
            icon={SwitcherNavIcon}
            onClick={(e) => {
              e.preventDefault();
              setAppSwitchOpen(true);
            }}
            title="Switch applications"
          />
        </RootRef>

        <RootRef rootRef={configMenuButton}>
          <SidebarNavLink
            classes={classes}
            className={classnames(
              classes.listItem,
              classes.lowerListItem,
              classes.companyListItem,
            )}
            hover={false}
            href="#none"
            icon={({ className }) => {
              const company = (
                <CompanyLogo
                  className={classnames(classes.companyIcon, className)}
                  domain={domain}
                  size={28}
                />
              );

              return company;
            }}
            onClick={(e) => {
              e.preventDefault();
              setConfigOpen(true);
            }}
            title="Switch Team"
          />
        </RootRef>

        <Popover
          anchorEl={appSwitchButton.current}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          classes={{ paper: classes.paper }}
          onClose={() => setAppSwitchOpen(false)}
          open={appSwitchOpen}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <AppSwitch />
        </Popover>

        <UserMenu
          popoverProps={{
            classes: { paper: classes.paper },
            open: configOpen,
            anchorEl: configMenuButton.current,
            anchorOrigin: { vertical: 'top', horizontal: 'left' },
            transformOrigin: { vertical: 'bottom', horizontal: 'left' },
            onClose: () => setConfigOpen(false),
          }}
        />
      </ul>
    </nav>
  );
};

const WrappedSidebar = (props) => (
  <Subscribe to={[UserContainer]}>
    {(userContainer) => <Sidebar {...props} userContainer={userContainer} />}
  </Subscribe>
);

Sidebar.propTypes = {
  beta: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  history: PropTypes.object,
  view: PropTypes.string,
  userContainer: PropTypes.shape({
    accountDomain: PropTypes.func.isRequired,
    shouldShowCompanies: PropTypes.func.isRequired,
    shouldShowICP: PropTypes.func.isRequired,
    shouldShowDashboard: PropTypes.func.isRequired,
    shouldShowSettings: PropTypes.func.isRequired,
    shouldShowPeople: PropTypes.func.isRequired,
    shouldShowForms: PropTypes.func.isRequired,
    hasNoAccess: PropTypes.func.isRequired,
    needsToOnboard: PropTypes.func.isRequired,
  }),
};

export default withRouter(withStyles(styles)(WrappedSidebar));
